import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Navigate, generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { ListWithDetailLayout } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG,
  FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG_SELECTEDUSERSLUG_ARENA,
} from '@sorare/core/src/constants/__generated__/routes';
import { DEFAULT_SEASON_SLUG } from '@sorare/core/src/constants/routes';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useTypedParams } from '@sorare/core/src/lib/routing';

import { NoLeaderboardEmptyState } from 'components/rivals/seasons/NoLeaderboardEmptyState';
import { NoSeasonEmptyState } from 'components/rivals/seasons/NoSeasonEmptyState';
import { SeasonLeaderboard } from 'components/rivals/seasons/SeasonLeaderboard';
import { SeasonLeaderboardHeader } from 'components/rivals/seasons/SeasonLeaderboard/SeasonLeaderboardHeader';
import { SeasonTimer } from 'components/rivals/seasons/SeasonTimer';

import {
  RivalsSeasonLeaderboardQuery,
  RivalsSeasonLeaderboardQueryVariables,
  SeasonsLeaderboardSeasonSlugUserSlugLayout_footballRivalsSeason,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const Root = styled(Vertical)`
  &.empty {
    height: calc(var(--area-height) - var(--navbar-height-mobile));
  }
`;
const StyledVertical = styled(Vertical)`
  width: 100%;
`;

const footballRivalsSeasonFragment = gql`
  fragment SeasonsLeaderboardSeasonSlugUserSlugLayout_footballRivalsSeason on FootballRivalsSeason {
    slug
    leaderboard(userSlug: $userSlug) {
      id
      endDate
      leaderboardConfig {
        divisionConfig {
          id
          title
        }
      }
      ...SeasonLeaderboard_footballRivalsDivisionLeaderboard
      ...SeasonLeaderboardHeader_footballRivalsDivisionLeaderboard
    }
  }
  ${SeasonLeaderboardHeader.fragments.footballRivalsDivisionLeaderboard}
  ${SeasonLeaderboard.fragments.footballRivalsDivisionLeaderboard}
` as TypedDocumentNode<SeasonsLeaderboardSeasonSlugUserSlugLayout_footballRivalsSeason>;
const RIVALS_SEASON_LEADERBOARD_QUERY = gql`
  query RivalsSeasonLeaderboardQuery(
    $isCurrentSeason: Boolean!
    $seasonSlug: String!
    $userSlug: String!
  ) {
    football {
      rivals {
        id
        manager(userSlug: $userSlug) {
          id
          nickname
        }
        leaderboardsConfig {
          ...SeasonLeaderboard_footballRivalsDivisionLeaderboardConfig
          ...SeasonLeaderboardHeader_footballRivalsDivisionLeaderboardConfig
        }
        currentSeason @include(if: $isCurrentSeason) {
          slug
          ...SeasonsLeaderboardSeasonSlugUserSlugLayout_footballRivalsSeason
        }
        season(slug: $seasonSlug) @skip(if: $isCurrentSeason) {
          slug
          ranking(userSlug: $userSlug) {
            id
            divisionChange
          }
          ...SeasonsLeaderboardSeasonSlugUserSlugLayout_footballRivalsSeason
        }
      }
    }
  }
  ${footballRivalsSeasonFragment}
  ${SeasonLeaderboardHeader.fragments.footballRivalsDivisionLeaderboardConfig}
  ${SeasonLeaderboard.fragments.footballRivalsDivisionLeaderboardConfig}
` as TypedDocumentNode<
  RivalsSeasonLeaderboardQuery,
  RivalsSeasonLeaderboardQueryVariables
>;

const SeasonsLeaderboardSeasonSlugUserSlugLayout = () => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const navigate = useNavigate();
  const { seasonSlug, userSlug, selectedUserSlug } =
    useTypedParams<LayoutParams>();

  const redirect = !selectedUserSlug && !!userSlug && isDesktopAndAbove;
  const isCurrentSeason = seasonSlug === DEFAULT_SEASON_SLUG;
  const { data, loading } = useQuery(RIVALS_SEASON_LEADERBOARD_QUERY, {
    variables: {
      isCurrentSeason,
      seasonSlug,
      userSlug,
    },
    skip: redirect,
  });
  const {
    season: fetchedSeason,
    currentSeason,
    manager,
    leaderboardsConfig,
  } = data?.football.rivals || {};
  const season = isCurrentSeason ? currentSeason : fetchedSeason;
  const { leaderboard } = season || {};

  if (redirect) {
    return (
      <Navigate
        to={generatePath(
          FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG_SELECTEDUSERSLUG_ARENA,
          {
            seasonSlug,
            userSlug,
            selectedUserSlug: userSlug,
          }
        )}
        replace
      />
    );
  }

  if (!data && loading) {
    return <LoadingIndicator fullHeight />;
  }

  if (!season) {
    return <NoSeasonEmptyState />;
  }

  if (!manager) {
    return null;
  }

  return (
    <>
      <PageHeader
        customTitle={
          <StyledVertical gap={0}>
            <SBHorizontal gap={2}>
              <LabelL bold as="h1">
                <FormattedMessage
                  id="RivalsLeaderboard.title"
                  defaultMessage="Weekly leaderboard"
                />
              </LabelL>
              {!isDesktopAndAbove && leaderboard && isCurrentSeason && (
                <SeasonTimer endDate={leaderboard.endDate} />
              )}
            </SBHorizontal>
            {isDesktopAndAbove && leaderboard && (
              <LabelM color="var(--c-neutral-600)">
                {leaderboard.leaderboardConfig.divisionConfig.title}
              </LabelM>
            )}
          </StyledVertical>
        }
      />
      <ListWithDetailLayout noPadding>
        <Root gap={0} className={classNames({ empty: !leaderboard })}>
          <SeasonLeaderboardHeader
            configs={leaderboardsConfig || []}
            seasonSlug={seasonSlug}
            userSlug={userSlug}
            onSeasonChange={slug =>
              navigate(
                generatePath(
                  FOOTBALL_PLAY_RIVALS_LEADERBOARD_SEASONSLUG_USERSLUG,
                  {
                    seasonSlug: slug,
                    userSlug,
                  }
                ),
                { replace: true }
              )
            }
            leaderboard={leaderboard}
          />
          {leaderboard ? (
            <SeasonLeaderboard
              featuredManagerId={manager.id}
              configs={leaderboardsConfig || []}
              seasonSlug={seasonSlug}
              userSlug={userSlug}
              selectedUserSlug={selectedUserSlug}
              userLeaderboard={leaderboard}
              isCurrentSeason={isCurrentSeason}
            />
          ) : (
            <NoLeaderboardEmptyState
              inactivity={
                !isCurrentSeason &&
                fetchedSeason?.ranking?.divisionChange ===
                  DivisionChange.RELEGATED
              }
            />
          )}
        </Root>
      </ListWithDetailLayout>
    </>
  );
};
export default SeasonsLeaderboardSeasonSlugUserSlugLayout;
